import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Input, Spin } from "antd";
import { InfoCircleOutlined, RightOutlined } from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroller";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

const SEARCH_THRESHOLD = 10;
const CHUNK_SIZE = 50;
const SELECT_ALL_MAX = 500;

function countSelectedForDimension(selections, dimension) {
  return Object.keys(selections?.[dimension] || {}).length;
}

export { default as WelcomePivotCompressedStepper } from "./CompressedStepper";

export default function WelcomePivotWizardStep({
  levelWiseOptions,
  levelWiseSelections,
  setLevelWiseSelections,
  loading = false,
}) {
  const { t } = useTranslation();
  const dimensions = levelWiseOptions?.level1 || [];
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [valueSearch, setValueSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const scrollParentRef = useRef(null);

  useEffect(() => {
    if (dimensions.length === 0) {
      if (selectedDimension != null) setSelectedDimension(null);
      return;
    }
    if (selectedDimension && dimensions.some((d) => d.id === selectedDimension)) {
      return;
    }
    setSelectedDimension(dimensions[0].id);
  }, [dimensions, selectedDimension]);

  useEffect(() => {
    setValueSearch("");
    setVisibleCount(CHUNK_SIZE);
  }, [selectedDimension]);

  const valueOptions = useMemo(() => {
    if (!selectedDimension) return [];
    return levelWiseOptions?.level2?.[selectedDimension] || [];
  }, [levelWiseOptions, selectedDimension]);

  const filteredValueOptions = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    if (!q) return valueOptions;
    return valueOptions.filter((opt) =>
      String(opt.label || opt.id || "")
        .toLowerCase()
        .includes(q),
    );
  }, [valueOptions, valueSearch]);

  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [valueSearch, filteredValueOptions.length]);

  const visibleItems = useMemo(
    () => filteredValueOptions.slice(0, visibleCount),
    [filteredValueOptions, visibleCount],
  );

  const hasMore = visibleCount < filteredValueOptions.length;

  const loadMore = useCallback(() => {
    if (visibleCount >= filteredValueOptions.length) return;
    setVisibleCount((prev) =>
      Math.min(prev + CHUNK_SIZE, filteredValueOptions.length),
    );
  }, [visibleCount, filteredValueOptions.length]);

  const selectedCount = countSelectedForDimension(
    levelWiseSelections,
    selectedDimension,
  );
  const allSelected =
    valueOptions.length > 0 && selectedCount === valueOptions.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const showSearch = valueOptions.length > SEARCH_THRESHOLD;
  const selectAllDisabled =
    valueOptions.length === 0 || valueOptions.length > SELECT_ALL_MAX;

  const handleToggleValue = useCallback(
    (valueId) => {
      if (!selectedDimension) return;
      setLevelWiseSelections((prev) => {
        const next = { ...prev };
        const keySelections = { ...(next[selectedDimension] || {}) };
        if (keySelections[valueId]) {
          delete keySelections[valueId];
        } else {
          keySelections[valueId] = {};
        }
        if (Object.keys(keySelections).length === 0) {
          delete next[selectedDimension];
        } else {
          next[selectedDimension] = keySelections;
        }
        return next;
      });
    },
    [selectedDimension, setLevelWiseSelections],
  );

  const handleSelectAll = useCallback(() => {
    if (!selectedDimension) return;
    setLevelWiseSelections((prev) => {
      const next = { ...prev };
      if (allSelected) {
        delete next[selectedDimension];
        return next;
      }
      next[selectedDimension] = Object.fromEntries(
        valueOptions.map((opt) => [opt.id, {}]),
      );
      return next;
    });
  }, [allSelected, selectedDimension, setLevelWiseSelections, valueOptions]);

  const handleRemoveSelection = useCallback(
    (dimension, valueId) => {
      setLevelWiseSelections((prev) => {
        const next = { ...prev };
        const keySelections = { ...(next[dimension] || {}) };
        delete keySelections[valueId];
        if (Object.keys(keySelections).length === 0) {
          delete next[dimension];
        } else {
          next[dimension] = keySelections;
        }
        return next;
      });
    },
    [setLevelWiseSelections],
  );

  const selectionPills = useMemo(() => {
    const pills = [];
    Object.entries(levelWiseSelections || {}).forEach(([dim, valMap]) => {
      Object.keys(valMap || {}).forEach((val) => {
        pills.push({ dimension: dim, value: val });
      });
    });
    return pills;
  }, [levelWiseSelections]);

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <Spin />
      </div>
    );
  }

  if (dimensions.length === 0) {
    return (
      <div className={styles.emptyWrap}>{t("welcome.pivotEmptyState")}</div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.infoBanner}>
        <InfoCircleOutlined className={styles.infoIcon} />
        <p className={styles.infoText}>{t("welcome.pivotWizardInfoBanner")}</p>
      </div>

      <div className={styles.twoPane}>
        <div className={styles.leftPane}>
          <div className={styles.paneHeader}>
            <span>{t("welcome.pivotWizardDimensionTitle")}</span>
          </div>
          <ul className={styles.dimensionList}>
            {dimensions.map((dim) => {
              const isActive = dim.id === selectedDimension;
              const dimSelected = countSelectedForDimension(
                levelWiseSelections,
                dim.id,
              );
              return (
                <li key={dim.id}>
                  <button
                    type="button"
                    className={
                      isActive ? styles.dimensionItemActive : styles.dimensionItem
                    }
                    onClick={() => setSelectedDimension(dim.id)}
                  >
                    <span className={styles.dimensionLabel}>{dim.label}</span>
                    <span className={styles.dimensionMeta}>
                      {dimSelected > 0 ? (
                        <span className={styles.dimCountBadge}>{dimSelected}</span>
                      ) : null}
                      {isActive ? (
                        <RightOutlined className={styles.dimensionChevron} />
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles.rightPane}>
          <div className={styles.paneHeader}>
            <span>{t("welcome.pivotWizardValueTitle")}</span>
            <span className={styles.valueCounter}>
              {visibleItems.length} / {filteredValueOptions.length}
              {filteredValueOptions.length !== valueOptions.length
                ? ` (${valueOptions.length})`
                : ""}
            </span>
          </div>
          <div className={styles.valuesBody}>
            {showSearch ? (
              <Input
                allowClear
                size="small"
                placeholder={`${t("button.5", "Ara")}...`}
                value={valueSearch}
                onChange={(e) => setValueSearch(e.target.value)}
                className={styles.valueSearch}
              />
            ) : null}
            <div className={styles.selectAllRow}>
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={handleSelectAll}
                disabled={selectAllDisabled}
              >
                {t("welcome.pivotWizardSelectAll")}
              </Checkbox>
            </div>
            <div ref={scrollParentRef} className={styles.valuesList}>
              {filteredValueOptions.length === 0 ? (
                <div className={styles.valuesEmpty}>
                  {t("pivot_filter.no_match")}
                </div>
              ) : (
                <InfiniteScroll
                  initialLoad={false}
                  pageStart={0}
                  loadMore={loadMore}
                  hasMore={hasMore}
                  useWindow={false}
                  getScrollParent={() => scrollParentRef.current}
                >
                  {visibleItems.map((opt) => (
                    <label key={opt.id} className={styles.valueRow}>
                      <Checkbox
                        checked={Boolean(
                          levelWiseSelections?.[selectedDimension]?.[opt.id],
                        )}
                        onChange={() => handleToggleValue(opt.id)}
                      />
                      <span className={styles.valueLabel}>{opt.label}</span>
                    </label>
                  ))}
                </InfiniteScroll>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectionPills.length > 0 ? (
        <div className={styles.selectionSummary}>
          <span className={styles.selectionSummaryLabel}>
            {t("welcome.pivotWizardSelectionLabel")}
          </span>
          {selectionPills.map(({ dimension, value }) => (
            <span key={`${dimension}-${value}`} className={styles.selectionPill}>
              <span>
                {dimension} = {value}
              </span>
              <button
                type="button"
                className={styles.selectionPillRemove}
                aria-label={t("welcome.pivotWizardRemoveSelection")}
                onClick={() => handleRemoveSelection(dimension, value)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
